import CloseIcon from '@mui/icons-material/Close';
import { Box, Fade, IconButton, Slide, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useMonthlyMenuStrings } from '../Order/userPackages/monthlyMenu-strings';

type NewMenuBannerProps = {
    onClose: () => void;
    isVisible: boolean;
};

export default function NewMenuBanner({ onClose, isVisible }: NewMenuBannerProps) {
    const monthlyStrings = useMonthlyMenuStrings();
    const [showBanner, setShowBanner] = useState(false);

    useEffect(() => {
        if (isVisible) {
            // Small delay to ensure smooth animation
            const timer = setTimeout(() => setShowBanner(true), 100);
            return () => clearTimeout(timer);
        } else {
            setShowBanner(false);
        }
    }, [isVisible]);

    if (!isVisible) return null;

    return (
        <Fade in={showBanner} timeout={800}>
            <Slide direction="down" in={showBanner} timeout={600}>
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px',
                        gap: '16px',
                        isolation: 'isolate',
                        height: '78px',
                        background: 'linear-gradient(90deg, #F3893E 0%, #F36767 100%)',
                        borderRadius: '7px',
                        flex: 'none',
                        order: 0,
                        alignSelf: 'stretch',
                        flexGrow: 0,
                        margin: '20px 0 40px',
                        overflow: 'hidden',
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                            '0%': {
                                boxShadow: '0 8px 32px rgba(243, 137, 62, 0.3)',
                            },
                            '50%': {
                                boxShadow: '0 12px 40px rgba(243, 137, 62, 0.5)',
                            },
                            '100%': {
                                boxShadow: '0 8px 32px rgba(243, 137, 62, 0.3)',
                            },
                        },
                        // Small screen adaptations
                        '@media (max-width: 768px)': {
                            flexDirection: 'row',
                            alignItems: 'flex-start',
                            height: 'auto',
                            minHeight: '78px',
                            padding: '12px',
                            gap: '8px',
                        },
                    }}
                >
                    {/* Left Block - Announce icon and text */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'flex-start',
                            gap: '8px',
                            flex: 1,
                            // Small screen adaptations
                            '@media (max-width: 768px)': {
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: '4px',
                                flex: 1,
                                marginRight: '140px',
                            },
                        }}
                    >
                        {/* Icon container */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: '10px',
                                width: '24px',
                                height: '26px',
                                flex: 'none',
                                // Small screen adaptations
                                '@media (max-width: 768px)': {
                                    width: 'auto',
                                    height: 'auto',
                                    gap: '6px',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                },
                            }}
                        >
                            <Box
                                component="img"
                                src="/img-assets/announce.svg"
                                alt="Announcement"
                                sx={{
                                    width: '24px',
                                    height: '24px',
                                    filter: 'brightness(0) invert(1)',
                                    flex: 'none',
                                    animation: 'bounce 2s ease-in-out infinite',
                                    '@keyframes bounce': {
                                        '0%, 20%, 50%, 80%, 100%': {
                                            transform: 'translateY(0)',
                                        },
                                        '40%': {
                                            transform: 'translateY(-10px)',
                                        },
                                        '60%': {
                                            transform: 'translateY(-5px)',
                                        },
                                    },
                                }}
                            />
                            {/* Title for small screens only */}
                            <Typography
                                sx={{
                                    display: 'none',
                                    // Small screen adaptations
                                    '@media (max-width: 768px)': {
                                        display: 'block',
                                        height: 'auto',
                                        fontFamily: 'Roboto',
                                        fontStyle: 'normal',
                                        fontWeight: 700,
                                        fontSize: '13px',
                                        lineHeight: '18px',
                                        color: '#FFFFFF',
                                        margin: 0,
                                        padding: 0,
                                    },
                                }}
                            >
                                {monthlyStrings.newMenuBannerTitle}
                            </Typography>
                        </Box>

                        {/* Text container */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                                padding: '0px',
                                height: '44px',
                                flex: 1,
                                // Small screen adaptations
                                '@media (max-width: 768px)': {
                                    height: 'auto',
                                    flex: 1,
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    height: '22px',
                                    fontFamily: 'Roboto',
                                    fontStyle: 'normal',
                                    fontWeight: 700,
                                    fontSize: '14px',
                                    lineHeight: '22px',
                                    color: '#FFFFFF',
                                    margin: 0,
                                    padding: 0,
                                    // Small screen adaptations
                                    '@media (max-width: 768px)': {
                                        display: 'none',
                                    },
                                }}
                            >
                                {monthlyStrings.newMenuBannerTitle}
                            </Typography>
                            <Typography
                                sx={{
                                    height: '22px',
                                    fontFamily: 'Roboto',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: '22px',
                                    color: '#FFFFFF',
                                    margin: '0 0 10px',
                                    padding: 0,
                                    // Small screen adaptations
                                    '@media (max-width: 768px)': {
                                        height: 'auto',
                                        fontSize: '12px',
                                        lineHeight: '16px',
                                    },
                                }}
                            >
                                {monthlyStrings.newMenuBannerBody}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Right Block - X button and vegetables */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: '8px',
                            flex: 'none',
                            minWidth: 'fit-content',
                            zIndex: 5,

                            // Small screen adaptations
                            '@media (max-width: 768px)': {
                                position: 'absolute',
                                right: '10px',
                                top: '0px',
                                bottom: '0px',
                                height: '100%',
                                flexDirection: 'column-reverse',
                                justifyContent: 'flex-start',
                                alignItems: 'flex-end',
                            },
                        }}
                    >
                        {/* Cancel button */}
                        <IconButton
                            onClick={onClose}
                            sx={{
                                width: '24px',
                                height: '24px',
                                color: '#F3893E',
                                backgroundColor: 'white',
                                border: 'none',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                },
                                flex: 'none',
                                zIndex: 10,
                                position: 'absolute',
                                top: '0px',
                                right: '0px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '15px',
                                // Small screen adaptations
                                '@media (max-width: 768px)': {
                                    width: '20px',
                                    height: '20px'
                                },
                            }}
                            size="small"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>

                        {/* Vegetables illustration */}
                        <Box
                            sx={{
                                width: '200px',
                                height: '96px',
                                flex: 'none',
                                marginRight: '10px',
                                // Small screen adaptations
                                '@media (max-width: 768px)': {
                                    width: '120px',
                                    height: '60px',
                                    alignSelf: 'flex-end',
                                },
                            }}
                        >
                            <Box
                                component="img"
                                src="/img-assets/veggies.svg"
                                alt="Vegetables"
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    // Small screen adaptations
                                    '@media (max-width: 768px)': {
                                        objectFit: 'unset',
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            </Slide>
        </Fade>
    );
}


